import {
  bulkDisablePowerUpBodySchema,
  bulkDisablePowerUpResponseSchema,
  boardPowerUpHeaderVisibilityBodySchema,
  boardPowerUpListSchema,
  boardPowerUpSchema,
  boardPowerUpSettingsBodySchema,
  disableBoardPowerUpBodySchema,
  enableBoardPowerUpBodySchema,
  powerUpAffectedBoardsResponseSchema,
  powerUpManifestSchema,
  powerUpDetailSchema,
  powerUpDirectoryQuerySchema,
  powerUpDirectoryResponseSchema,
  powerUpPolicyPatchSchema,
  powerUpPolicySchema,
  powerUpUsageResponseSchema,
  removePersonalPowerUpSettingsBodySchema,
  ulidSchema,
  type ApiError,
  type BoardPowerUp,
  type BoardPowerUpList,
  type BulkDisablePowerUpResponse,
  type PowerUpAffectedBoardsResponse,
  type PowerUpAuthorizationState,
  type PowerUpCategory,
  type PowerUpDetail,
  type PowerUpDirectoryResponse,
  type PowerUpPolicy,
  type PowerUpUsageResponse,
  type PowerUpPricingModel,
} from '@molt/shared';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';
import { z } from 'zod';
import type { infer as zInfer } from 'zod';
import { del, emptyResponseSchema, get, patch, post, request } from './api';
import { queryKeys } from './query-keys';

type PowerUpDirectoryQuery = zInfer<typeof powerUpDirectoryQuerySchema>;
type EnableBoardPowerUpBody = zInfer<typeof enableBoardPowerUpBodySchema>;
type BoardPowerUpSettingsBody = zInfer<typeof boardPowerUpSettingsBodySchema>;
type DisableBoardPowerUpBody = zInfer<typeof disableBoardPowerUpBodySchema>;
export type PowerUpPolicyPatch = zInfer<typeof powerUpPolicyPatchSchema>;
export type PowerUpJsonObject = zInfer<typeof boardPowerUpSettingsBodySchema>['settings'];
type PowerUpManifestEntry = zInfer<
  typeof powerUpManifestSchema
>['cardFrontBadges'][number];

export const surfaceManifestResponseSchema = z.strictObject({
  boardId: ulidSchema,
  powerUps: z.array(
    z.strictObject({
      id: ulidSchema,
      boardPowerUpId: ulidSchema,
      name: z.string().min(1),
      headerButtonVisible: z.boolean(),
      authorizationState: z.enum([
        'not_required',
        'connect_required',
        'authorized',
        'reconnect_required',
      ]),
      manifest: powerUpManifestSchema,
    }),
  ),
});

export type SurfaceManifestResponse = zInfer<typeof surfaceManifestResponseSchema>;

export interface PowerUpSurfaceRuntimeEntry {
  readonly id: string;
  readonly key: string;
  readonly label: string;
  readonly description?: string | undefined;
  readonly emptyState?: string | undefined;
  readonly powerUpName: string;
  readonly boardPowerUpId: string;
  readonly authorizationState: PowerUpAuthorizationState;
}

export interface PowerUpSurfaceSummary {
  readonly cardFrontBadges: readonly PowerUpSurfaceRuntimeEntry[];
  readonly cardBackBadges: readonly PowerUpSurfaceRuntimeEntry[];
  readonly cardBackSections: readonly PowerUpSurfaceRuntimeEntry[];
  readonly cardBackButtons: readonly PowerUpSurfaceRuntimeEntry[];
  readonly boardHeaderButtons: readonly PowerUpSurfaceRuntimeEntry[];
  readonly listMenuActions: readonly PowerUpSurfaceRuntimeEntry[];
  readonly listMenuSorts: readonly PowerUpSurfaceRuntimeEntry[];
}

const EMPTY_POWER_UP_SURFACE_SUMMARY: PowerUpSurfaceSummary = {
  cardFrontBadges: [],
  cardBackBadges: [],
  cardBackSections: [],
  cardBackButtons: [],
  boardHeaderButtons: [],
  listMenuActions: [],
  listMenuSorts: [],
};

const CALENDAR_POWER_UP_NAME = 'calendar';
const CALENDAR_POWER_UP_HEADER_KEYS = new Set([
  'calendar',
  'calendar-power-up',
  'month-grid',
  'open-calendar',
]);

export interface PowerUpDirectoryRequest {
  readonly q?: string | null | undefined;
  readonly category?: PowerUpCategory | null | undefined;
  readonly madeByUs?: boolean | null | undefined;
  readonly pricingModel?: PowerUpPricingModel | null | undefined;
  readonly boardId?: string | null | undefined;
}

export function parsePowerUpDirectoryRequest(
  request: PowerUpDirectoryRequest = {},
): PowerUpDirectoryQuery {
  const q = request.q?.trim();
  return powerUpDirectoryQuerySchema.parse({
    ...(q === undefined || q === '' ? {} : { q }),
    ...(request.category == null ? {} : { category: request.category }),
    ...(request.madeByUs == null ? {} : { madeByUs: request.madeByUs }),
    ...(request.pricingModel == null ? {} : { pricingModel: request.pricingModel }),
    ...(request.boardId == null ? {} : { boardId: request.boardId }),
  });
}

export function powerUpDirectorySearchParams(
  request: PowerUpDirectoryRequest = {},
): URLSearchParams {
  const parsed = parsePowerUpDirectoryRequest(request);
  const params = new URLSearchParams();
  if (parsed.q !== undefined) params.set('q', parsed.q);
  if (parsed.category !== undefined) params.set('category', parsed.category);
  if (parsed.madeByUs !== undefined) params.set('madeByUs', String(parsed.madeByUs));
  if (parsed.pricingModel !== undefined) {
    params.set('pricingModel', parsed.pricingModel);
  }
  if (parsed.boardId !== undefined) params.set('boardId', parsed.boardId);
  return params;
}

export function powerUpDirectoryQueryString(
  request: PowerUpDirectoryRequest = {},
): string {
  return powerUpDirectorySearchParams(request).toString();
}

export function getPowerUpDirectory(
  request: PowerUpDirectoryRequest = {},
): Promise<PowerUpDirectoryResponse> {
  const query = powerUpDirectoryQueryString(request);
  return get(
    query === '' ? '/power-ups' : `/power-ups?${query}`,
    powerUpDirectoryResponseSchema,
  );
}

export function powerUpDirectoryQueryOptions(
  request: PowerUpDirectoryRequest = {},
): UseQueryOptions<PowerUpDirectoryResponse, ApiError> {
  const query = powerUpDirectoryQueryString(request);
  return {
    queryKey: queryKeys.powerUps.directory(query),
    queryFn: () => getPowerUpDirectory(request),
  };
}

function rawPowerUpDirectoryKey(request: PowerUpDirectoryRequest = {}): string {
  const params = new URLSearchParams();
  const q = request.q?.trim();
  if (q !== undefined && q !== '') params.set('q', q);
  if (request.category != null) params.set('category', request.category);
  if (request.madeByUs != null) params.set('madeByUs', String(request.madeByUs));
  if (request.pricingModel != null) params.set('pricingModel', request.pricingModel);
  if (request.boardId != null) params.set('boardId', request.boardId);
  return params.toString();
}

export function usePowerUpDirectory(
  request: PowerUpDirectoryRequest = {},
  enabled = true,
): UseQueryResult<PowerUpDirectoryResponse, ApiError> {
  if (!enabled) {
    return useQuery({
      queryKey: queryKeys.powerUps.directory(rawPowerUpDirectoryKey(request)),
      queryFn: () => getPowerUpDirectory(request),
      enabled: false,
    });
  }
  return useQuery({
    ...powerUpDirectoryQueryOptions(request),
    enabled,
  });
}

export function getPowerUpDetail(powerUpId: string): Promise<PowerUpDetail> {
  return get(`/power-ups/${encodeURIComponent(powerUpId)}`, powerUpDetailSchema);
}

export function powerUpDetailQueryOptions(
  powerUpId: string,
): UseQueryOptions<PowerUpDetail, ApiError> {
  return {
    queryKey: queryKeys.powerUps.detail(powerUpId),
    queryFn: () => getPowerUpDetail(powerUpId),
  };
}

export function usePowerUpDetail(
  powerUpId: string,
  enabled: boolean,
): UseQueryResult<PowerUpDetail, ApiError> {
  return useQuery({
    ...powerUpDetailQueryOptions(powerUpId),
    enabled,
  });
}

export function getBoardPowerUps(boardId: string): Promise<BoardPowerUpList> {
  return get(`/boards/${encodeURIComponent(boardId)}/power-ups`, boardPowerUpListSchema);
}

export function boardPowerUpsQueryOptions(
  boardId: string,
): UseQueryOptions<BoardPowerUpList, ApiError> {
  return {
    queryKey: queryKeys.powerUps.board(boardId),
    queryFn: () => getBoardPowerUps(boardId),
  };
}

export function useBoardPowerUps(
  boardId: string,
  enabled: boolean,
): UseQueryResult<BoardPowerUpList, ApiError> {
  return useQuery({
    ...boardPowerUpsQueryOptions(boardId),
    enabled,
  });
}

export function getBoardPowerUpSurfaces(
  boardId: string,
): Promise<SurfaceManifestResponse> {
  return get(
    `/boards/${encodeURIComponent(boardId)}/power-ups/surfaces`,
    surfaceManifestResponseSchema,
  );
}

export function boardPowerUpSurfacesQueryOptions(
  boardId: string,
): UseQueryOptions<SurfaceManifestResponse, ApiError> {
  return {
    queryKey: queryKeys.powerUps.boardSurfaces(boardId),
    queryFn: () => getBoardPowerUpSurfaces(boardId),
  };
}

export function useBoardPowerUpSurfaces(
  boardId: string,
  enabled: boolean,
): UseQueryResult<SurfaceManifestResponse, ApiError> {
  return useQuery({
    ...boardPowerUpSurfacesQueryOptions(boardId),
    enabled,
  });
}

export function getEnterprisePowerUpPolicy(enterpriseId: string): Promise<PowerUpPolicy> {
  return get(
    `/enterprises/${encodeURIComponent(enterpriseId)}/power-ups/policy`,
    powerUpPolicySchema,
  );
}

export function updateEnterprisePowerUpPolicy(
  enterpriseId: string,
  body: PowerUpPolicyPatch,
): Promise<PowerUpPolicy> {
  return patch(
    `/enterprises/${encodeURIComponent(enterpriseId)}/power-ups/policy`,
    powerUpPolicySchema,
    powerUpPolicyPatchSchema.parse(body),
  );
}

export function getEnterprisePowerUpUsage(
  enterpriseId: string,
): Promise<PowerUpUsageResponse> {
  return get(
    `/enterprises/${encodeURIComponent(enterpriseId)}/power-ups/usage`,
    powerUpUsageResponseSchema,
  );
}

export function getEnterprisePowerUpAffectedBoards(
  enterpriseId: string,
  powerUpId: string,
): Promise<PowerUpAffectedBoardsResponse> {
  return get(
    `/enterprises/${encodeURIComponent(enterpriseId)}/power-ups/${encodeURIComponent(powerUpId)}/affected-boards`,
    powerUpAffectedBoardsResponseSchema,
  );
}

export function bulkDisableEnterprisePowerUp(
  enterpriseId: string,
  powerUpId: string,
  keepData: boolean,
): Promise<BulkDisablePowerUpResponse> {
  return post(
    `/enterprises/${encodeURIComponent(enterpriseId)}/power-ups/${encodeURIComponent(powerUpId)}/bulk-disable`,
    bulkDisablePowerUpResponseSchema,
    bulkDisablePowerUpBodySchema.parse({ keepData }),
  );
}

export function enterprisePowerUpPolicyQueryOptions(
  enterpriseId: string,
): UseQueryOptions<PowerUpPolicy, ApiError> {
  return {
    queryKey: queryKeys.powerUps.enterprisePolicy(enterpriseId),
    queryFn: () => getEnterprisePowerUpPolicy(enterpriseId),
  };
}

export function enterprisePowerUpUsageQueryOptions(
  enterpriseId: string,
): UseQueryOptions<PowerUpUsageResponse, ApiError> {
  return {
    queryKey: queryKeys.powerUps.enterpriseUsage(enterpriseId),
    queryFn: () => getEnterprisePowerUpUsage(enterpriseId),
  };
}

export function enterprisePowerUpAffectedBoardsQueryOptions(
  enterpriseId: string,
  powerUpId: string,
): UseQueryOptions<PowerUpAffectedBoardsResponse, ApiError> {
  return {
    queryKey: queryKeys.powerUps.enterpriseAffectedBoards(enterpriseId, powerUpId),
    queryFn: () => getEnterprisePowerUpAffectedBoards(enterpriseId, powerUpId),
  };
}

export function useEnterprisePowerUpPolicy(
  enterpriseId: string,
  enabled: boolean,
): UseQueryResult<PowerUpPolicy, ApiError> {
  return useQuery({
    ...enterprisePowerUpPolicyQueryOptions(enterpriseId),
    enabled,
  });
}

export function useEnterprisePowerUpUsage(
  enterpriseId: string,
  enabled: boolean,
): UseQueryResult<PowerUpUsageResponse, ApiError> {
  return useQuery({
    ...enterprisePowerUpUsageQueryOptions(enterpriseId),
    enabled,
  });
}

export function useEnterprisePowerUpAffectedBoards(
  enterpriseId: string,
  powerUpId: string | null,
): UseQueryResult<PowerUpAffectedBoardsResponse, ApiError> {
  return useQuery({
    ...enterprisePowerUpAffectedBoardsQueryOptions(enterpriseId, powerUpId ?? ''),
    enabled: powerUpId !== null,
  });
}

export function enableBoardPowerUp(
  boardId: string,
  powerUpId: string,
  body: EnableBoardPowerUpBody = {},
): Promise<BoardPowerUp> {
  return post(
    `/boards/${encodeURIComponent(boardId)}/power-ups/${encodeURIComponent(powerUpId)}`,
    boardPowerUpSchema,
    enableBoardPowerUpBodySchema.parse(body),
  );
}

export function updateBoardPowerUpHeaderVisibility(
  boardPowerUpId: string,
  visible: boolean,
): Promise<null> {
  return patch(
    `/board-power-ups/${encodeURIComponent(boardPowerUpId)}/header-button-visible`,
    emptyResponseSchema,
    boardPowerUpHeaderVisibilityBodySchema.parse({ visible }),
  );
}

export function updateBoardPowerUpSettings(
  boardPowerUpId: string,
  settings: BoardPowerUpSettingsBody['settings'],
): Promise<null> {
  return patch(
    `/board-power-ups/${encodeURIComponent(boardPowerUpId)}/settings`,
    emptyResponseSchema,
    boardPowerUpSettingsBodySchema.parse({ settings }),
  );
}

export function authorizeBoardPowerUp(boardPowerUpId: string): Promise<null> {
  return post(
    `/board-power-ups/${encodeURIComponent(boardPowerUpId)}/authorizations`,
    emptyResponseSchema,
  );
}

export function revokeBoardPowerUpAuthorization(boardPowerUpId: string): Promise<null> {
  return del(
    `/board-power-ups/${encodeURIComponent(boardPowerUpId)}/authorizations/me`,
    emptyResponseSchema,
  );
}

export function disableBoardPowerUp(
  boardPowerUpId: string,
  keepData: boolean,
): Promise<null> {
  return request(
    `/board-power-ups/${encodeURIComponent(boardPowerUpId)}`,
    emptyResponseSchema,
    {
      method: 'DELETE',
      body: JSON.stringify(disableBoardPowerUpBodySchema.parse({ keepData })),
    },
  );
}

export function removePersonalPowerUpSettings(boardPowerUpId: string): Promise<null> {
  return del(
    `/board-power-ups/${encodeURIComponent(boardPowerUpId)}/personal-settings`,
    emptyResponseSchema,
    removePersonalPowerUpSettingsBodySchema.parse({}),
  );
}

function invalidateBoardPowerUpState(
  queryClient: ReturnType<typeof useQueryClient>,
  boardId: string,
): void {
  void queryClient.invalidateQueries({ queryKey: queryKeys.powerUps.all() });
  void queryClient.invalidateQueries({ queryKey: queryKeys.powerUps.board(boardId) });
  void queryClient.invalidateQueries({
    queryKey: queryKeys.powerUps.boardSurfaces(boardId),
  });
}

function invalidateEnterprisePowerUpPolicy(
  queryClient: ReturnType<typeof useQueryClient>,
  enterpriseId: string,
): void {
  void queryClient.invalidateQueries({
    queryKey: queryKeys.powerUps.enterprisePolicy(enterpriseId),
  });
  void queryClient.invalidateQueries({ queryKey: queryKeys.powerUps.all() });
}

function invalidateEnterprisePowerUpUsage(
  queryClient: ReturnType<typeof useQueryClient>,
  enterpriseId: string,
): void {
  void queryClient.invalidateQueries({
    queryKey: queryKeys.powerUps.enterpriseUsage(enterpriseId),
  });
  void queryClient.invalidateQueries({
    queryKey: queryKeys.powerUps.enterpriseAffectedBoardsAll(enterpriseId),
  });
  void queryClient.invalidateQueries({ queryKey: queryKeys.powerUps.all() });
}

export function useUpdateEnterprisePowerUpPolicy(
  enterpriseId: string,
): UseMutationResult<PowerUpPolicy, ApiError, PowerUpPolicyPatch> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) => updateEnterprisePowerUpPolicy(enterpriseId, body),
    onSuccess: (policy) => {
      queryClient.setQueryData(queryKeys.powerUps.enterprisePolicy(enterpriseId), policy);
      invalidateEnterprisePowerUpPolicy(queryClient, enterpriseId);
    },
  });
}

export function useBulkDisableEnterprisePowerUp(
  enterpriseId: string,
): UseMutationResult<
  BulkDisablePowerUpResponse,
  ApiError,
  { readonly powerUpId: string; readonly keepData: boolean }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ powerUpId, keepData }) =>
      bulkDisableEnterprisePowerUp(enterpriseId, powerUpId, keepData),
    onSuccess: () => {
      invalidateEnterprisePowerUpUsage(queryClient, enterpriseId);
    },
  });
}

export function useEnableBoardPowerUp(
  boardId: string,
): UseMutationResult<
  BoardPowerUp,
  ApiError,
  { readonly powerUpId: string; readonly body?: EnableBoardPowerUpBody | undefined }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ powerUpId, body }) => enableBoardPowerUp(boardId, powerUpId, body),
    onSuccess: (enabled) => {
      queryClient.setQueryData<BoardPowerUpList>(
        queryKeys.powerUps.board(boardId),
        (previous) => ({
          powerUps: [
            enabled,
            ...(previous?.powerUps.filter((entry) => entry.id !== enabled.id) ?? []),
          ],
        }),
      );
      invalidateBoardPowerUpState(queryClient, boardId);
    },
  });
}

export function useUpdateBoardPowerUpHeaderVisibility(
  boardId: string,
): UseMutationResult<
  null,
  ApiError,
  { readonly boardPowerUpId: string; readonly visible: boolean }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ boardPowerUpId, visible }) =>
      updateBoardPowerUpHeaderVisibility(boardPowerUpId, visible),
    onSuccess: (_result, variables) => {
      queryClient.setQueryData<BoardPowerUpList>(
        queryKeys.powerUps.board(boardId),
        (previous) =>
          previous === undefined
            ? previous
            : {
                powerUps: previous.powerUps.map((entry) =>
                  entry.id === variables.boardPowerUpId
                    ? { ...entry, headerButtonVisible: variables.visible }
                    : entry,
                ),
              },
      );
      invalidateBoardPowerUpState(queryClient, boardId);
    },
  });
}

export function useUpdateBoardPowerUpSettings(boardId: string): UseMutationResult<
  null,
  ApiError,
  {
    readonly boardPowerUpId: string;
    readonly settings: BoardPowerUpSettingsBody['settings'];
  }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ boardPowerUpId, settings }) =>
      updateBoardPowerUpSettings(boardPowerUpId, settings),
    onSuccess: (_result, variables) => {
      queryClient.setQueryData<BoardPowerUpList>(
        queryKeys.powerUps.board(boardId),
        (previous) =>
          previous === undefined
            ? previous
            : {
                powerUps: previous.powerUps.map((entry) =>
                  entry.id === variables.boardPowerUpId
                    ? { ...entry, settings: variables.settings }
                    : entry,
                ),
              },
      );
      invalidateBoardPowerUpState(queryClient, boardId);
    },
  });
}

export function useAuthorizeBoardPowerUp(
  boardId: string,
): UseMutationResult<null, ApiError, { readonly boardPowerUpId: string }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ boardPowerUpId }) => authorizeBoardPowerUp(boardPowerUpId),
    onSuccess: (_result, variables) => {
      queryClient.setQueryData<BoardPowerUpList>(
        queryKeys.powerUps.board(boardId),
        (previous) =>
          previous === undefined
            ? previous
            : {
                powerUps: previous.powerUps.map((entry) =>
                  entry.id === variables.boardPowerUpId
                    ? { ...entry, authorizationState: 'authorized' }
                    : entry,
                ),
              },
      );
      invalidateBoardPowerUpState(queryClient, boardId);
    },
  });
}

export function useRevokeBoardPowerUpAuthorization(
  boardId: string,
): UseMutationResult<null, ApiError, { readonly boardPowerUpId: string }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ boardPowerUpId }) => revokeBoardPowerUpAuthorization(boardPowerUpId),
    onSuccess: (_result, variables) => {
      queryClient.setQueryData<BoardPowerUpList>(
        queryKeys.powerUps.board(boardId),
        (previous) =>
          previous === undefined
            ? previous
            : {
                powerUps: previous.powerUps.map((entry) =>
                  entry.id === variables.boardPowerUpId
                    ? { ...entry, authorizationState: 'connect_required' }
                    : entry,
                ),
              },
      );
      invalidateBoardPowerUpState(queryClient, boardId);
    },
  });
}

export function useDisableBoardPowerUp(boardId: string): UseMutationResult<
  null,
  ApiError,
  {
    readonly boardPowerUpId: string;
    readonly keepData: DisableBoardPowerUpBody['keepData'];
  }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ boardPowerUpId, keepData }) =>
      disableBoardPowerUp(boardPowerUpId, keepData),
    onSuccess: (_result, variables) => {
      queryClient.setQueryData<BoardPowerUpList>(
        queryKeys.powerUps.board(boardId),
        (previous) =>
          previous === undefined
            ? previous
            : {
                powerUps: previous.powerUps.filter(
                  (entry) => entry.id !== variables.boardPowerUpId,
                ),
              },
      );
      invalidateBoardPowerUpState(queryClient, boardId);
    },
  });
}

export function useRemovePersonalPowerUpSettings(
  boardId: string,
): UseMutationResult<null, ApiError, { readonly boardPowerUpId: string }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ boardPowerUpId }) => removePersonalPowerUpSettings(boardPowerUpId),
    onSuccess: () => {
      invalidateBoardPowerUpState(queryClient, boardId);
    },
  });
}

function runtimeEntries(
  powerUp: SurfaceManifestResponse['powerUps'][number],
  surface: keyof PowerUpSurfaceSummary,
  entries: readonly PowerUpManifestEntry[],
): PowerUpSurfaceRuntimeEntry[] {
  return entries.map((entry) => ({
    id: `${powerUp.boardPowerUpId}:${surface}:${entry.key}`,
    key: entry.key,
    label: entry.label,
    ...(entry.description === undefined ? {} : { description: entry.description }),
    ...(entry.emptyState === undefined ? {} : { emptyState: entry.emptyState }),
    powerUpName: powerUp.name,
    boardPowerUpId: powerUp.boardPowerUpId,
    authorizationState: powerUp.authorizationState,
  }));
}

export function powerUpSurfaceSummary(
  surfaces: SurfaceManifestResponse | undefined,
): PowerUpSurfaceSummary {
  if (surfaces === undefined || surfaces.powerUps.length === 0) {
    return EMPTY_POWER_UP_SURFACE_SUMMARY;
  }

  return surfaces.powerUps.reduce<PowerUpSurfaceSummary>(
    (summary, powerUp) => ({
      cardFrontBadges: [
        ...summary.cardFrontBadges,
        ...runtimeEntries(powerUp, 'cardFrontBadges', powerUp.manifest.cardFrontBadges),
      ],
      cardBackBadges: [
        ...summary.cardBackBadges,
        ...runtimeEntries(powerUp, 'cardBackBadges', powerUp.manifest.cardBackBadges),
      ],
      cardBackSections: [
        ...summary.cardBackSections,
        ...runtimeEntries(powerUp, 'cardBackSections', powerUp.manifest.cardBackSections),
      ],
      cardBackButtons: [
        ...summary.cardBackButtons,
        ...runtimeEntries(powerUp, 'cardBackButtons', powerUp.manifest.cardBackButtons),
      ],
      boardHeaderButtons: [
        ...summary.boardHeaderButtons,
        ...(powerUp.headerButtonVisible
          ? runtimeEntries(
              powerUp,
              'boardHeaderButtons',
              powerUp.manifest.boardHeaderButtons,
            )
          : []),
      ],
      listMenuActions: [
        ...summary.listMenuActions,
        ...runtimeEntries(powerUp, 'listMenuActions', powerUp.manifest.listMenuActions),
      ],
      listMenuSorts: [
        ...summary.listMenuSorts,
        ...runtimeEntries(powerUp, 'listMenuSorts', powerUp.manifest.listMenuSorts),
      ],
    }),
    EMPTY_POWER_UP_SURFACE_SUMMARY,
  );
}

export function isCalendarPowerUpHeaderEntry(entry: PowerUpSurfaceRuntimeEntry): boolean {
  return (
    entry.powerUpName.trim().toLowerCase() === CALENDAR_POWER_UP_NAME &&
    CALENDAR_POWER_UP_HEADER_KEYS.has(entry.key.trim().toLowerCase())
  );
}

export function calendarPowerUpHeaderEntry(
  summary: PowerUpSurfaceSummary,
): PowerUpSurfaceRuntimeEntry | null {
  return summary.boardHeaderButtons.find(isCalendarPowerUpHeaderEntry) ?? null;
}
